import React from 'react';
import { DS } from './ds';
// ============================================================
// PRIMITIVE: Button
// ============================================================
type BtnVariant = 'primary' | 'secondary' | 'aqua' | 'ghost' | 'white' | 'kraftOutline';
interface BtnProps {
  children: React.ReactNode;
  variant?: BtnVariant;
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  type?: 'button' | 'submit';
  disabled?: boolean;
}
export const Btn = ({
  children,
  variant = 'primary',
  size = 'md',
  href,
  onClick,
  icon,
  fullWidth,
  type = 'button',
  disabled = false
}: BtnProps) => {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '9px',
    borderRadius: '100px',
    fontFamily: DS.font.body,
    fontWeight: 600,
    cursor: 'pointer',
    transition: `all 0.25s ${DS.ease.smooth}`,
    border: 'none',
    textDecoration: 'none',
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : undefined
  };
  const sizes: Record<string, React.CSSProperties> = {
    sm: {
      padding: '9px 22px',
      fontSize: '13px'
    },
    md: {
      padding: '13px 30px',
      fontSize: '15px'
    },
    lg: {
      padding: '17px 40px',
      fontSize: '16px'
    }
  };
  const variants: Record<BtnVariant, React.CSSProperties> = {
    primary: {
      background: '#00AAD4',
      color: '#fff',
      boxShadow: '0 8px 26px rgba(0, 170, 212, 0.42)'
    },
    secondary: {
      background: 'transparent',
      color: DS.color.tealDeep,
      border: `1.5px solid ${DS.color.tealDeep}`
    },
    aqua: {
      background: `linear-gradient(135deg, ${DS.color.aquaLight} 0%, ${DS.color.aqua} 100%)`,
      color: DS.color.tealDeep,
      fontWeight: 700,
      boxShadow: '0 4px 20px rgba(75,199,210,0.4)'
    },
    ghost: {
      background: 'rgba(255,255,255,0.08)',
      color: '#fff',
      border: '1.5px solid rgba(255,255,255,0.2)',
      backdropFilter: 'blur(10px)'
    },
    white: {
      background: '#fff',
      color: DS.color.tealDeep,
      boxShadow: '0 4px 20px rgba(13,59,80,0.15)'
    },
    kraftOutline: {
      background: 'transparent',
      color: DS.color.kraft,
      border: `1.5px solid ${DS.color.kraft}`
    }
  };
  const s = {
    ...base,
    ...sizes[size],
    ...variants[variant],
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.65 : 1
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    e.currentTarget.style.transform = 'translateY(-2px)';
    if (variant === 'primary') {
      e.currentTarget.style.background = '#0099CC';
      e.currentTarget.style.boxShadow = '0 12px 34px rgba(0, 170, 212, 0.55)';
    }
    if (variant === 'aqua') e.currentTarget.style.boxShadow = '0 10px 32px rgba(75,199,210,0.55)';
    if (variant === 'secondary') {
      e.currentTarget.style.background = DS.color.tealDeep;
      e.currentTarget.style.color = '#fff';
    }
    if (variant === 'white') e.currentTarget.style.boxShadow = '0 10px 32px rgba(13,59,80,0.25)';
    if (variant === 'kraftOutline') {
      e.currentTarget.style.background = DS.color.kraft;
      e.currentTarget.style.color = '#fff';
    }
  };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = variants[variant].boxShadow as string || '';
    if (variant === 'primary') e.currentTarget.style.background = '#00AAD4';
    if (variant === 'secondary') {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = DS.color.tealDeep;
    }
    if (variant === 'kraftOutline') {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = DS.color.kraft;
    }
  };
  const props = {
    style: s,
    onMouseEnter: onEnter,
    onMouseLeave: onLeave
  };
  return href ? <a href={disabled ? undefined : href} {...props} target="_blank" rel="noopener noreferrer" aria-disabled={disabled || undefined} data-id="element-110" data-mp="dqschqbukmqa">
      {icon && icon}
      {children}
    </a> : <button type={type} onClick={onClick} disabled={disabled} {...props} data-id="element-111" data-mp="3vyhulq7h46gb">
      {icon && icon}
      {children}
    </button>;
};
// ============================================================
// PRIMITIVE: Section label
// ============================================================
export const Label = ({
  children,
  light = false
}: {
  children: React.ReactNode;
  light?: boolean;
}) => <div style={{
  marginBottom: '16px'
}} data-id="element-112" data-mp="iry57o5ic9zt">
    <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '5px 14px',
    borderRadius: '100px',
    background: light ? 'rgba(75,199,210,0.12)' : DS.color.mint,
    color: light ? DS.color.aquaLight : DS.color.teal,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontFamily: DS.font.body
  }} data-id="element-113" data-mp="irg9qtqo5zsy">
      <span style={{
      width: '5px',
      height: '5px',
      borderRadius: '50%',
      background: light ? DS.color.aquaLight : DS.color.teal,
      display: 'inline-block'
    }} data-id="element-114" data-mp="326apg29mbvtw" />
      {children}
    </span>
  </div>;
// ============================================================
// PRIMITIVE: Headings + paragraph
// ============================================================
type HSize = 'hero' | 'xl' | 'lg' | 'md' | 'sm';
export const H = ({
  children,
  level = 2,
  size = 'lg',
  light = false,
  align = 'left'
}: {
  children: React.ReactNode;
  level?: 1 | 2 | 3 | 4;
  size?: HSize;
  light?: boolean;
  align?: React.CSSProperties['textAlign'];
}) => {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  const sizes: Record<HSize, string> = {
    hero: 'clamp(2.8rem, 6vw, 4.4rem)',
    xl: 'clamp(2.4rem, 5vw, 3.6rem)',
    lg: 'clamp(1.9rem, 4vw, 2.8rem)',
    md: 'clamp(1.4rem, 3vw, 2rem)',
    sm: 'clamp(1.1rem, 2vw, 1.4rem)'
  };
  return <Tag style={{
    fontFamily: DS.font.heading,
    fontWeight: 700,
    fontSize: sizes[size],
    lineHeight: 1.12,
    letterSpacing: '-0.025em',
    textAlign: align,
    color: light ? '#fff' : DS.color.ink,
    margin: 0
  }} data-id="element-115" data-mp="29io20luaaa87">
      {children}
    </Tag>;
};
export const P = ({
  children,
  light = false,
  size = 'base',
  align = 'left',
  maxWidth
}: {
  children: React.ReactNode;
  light?: boolean;
  size?: 'sm' | 'base' | 'lg';
  align?: React.CSSProperties['textAlign'];
  maxWidth?: string;
}) => <p style={{
  fontFamily: DS.font.body,
  fontSize: {
    sm: '13px',
    base: '1.05rem',
    lg: '1.15rem'
  }[size],
  color: light ? 'rgba(255,255,255,0.62)' : DS.color.muted,
  lineHeight: 1.75,
  textAlign: align,
  maxWidth: maxWidth || 'none',
  margin: 0
}} data-id="element-116" data-mp="y2h7w2stxckp">
    {children}
  </p>;